import { getDesktopBridge, isDesktopPlatform } from '@/lib/desktop/bridge';

import type { Session, SupabaseClient, User } from '@supabase/supabase-js';

interface LegacyStoredSession {
  currentSession?: Session;
  session?: Session;
  access_token?: string;
  refresh_token?: string;
  user?: User;
}

type SessionWithTokens = Session & {
  access_token: string;
  refresh_token: string;
};

/** Storage key the Supabase client persists its auth session under (`sb-<ref>-auth-token`). */
export function getSupabaseAuthTokenKey(url: string): string {
  try {
    const { hostname } = new URL(url);
    const projectId = hostname.split('.')[0];
    return `sb-${projectId}-auth-token`;
  } catch {
    throw new Error('Invalid Supabase URL');
  }
}

function hasSessionTokens(session: Session | null | undefined): session is SessionWithTokens {
  return Boolean(session?.access_token && session.refresh_token);
}

function readLocalStorageSession(): Session | null {
  try {
    const storageKey = getSupabaseAuthTokenKey(import.meta.env.VITE_SUPABASE_URL);
    const storedSession = globalThis.localStorage.getItem(storageKey);
    if (!storedSession) {
      return null;
    }

    const parsed = JSON.parse(storedSession) as LegacyStoredSession;
    if (parsed.currentSession) {
      return parsed.currentSession;
    }
    if (parsed.session) {
      return parsed.session;
    }
    if (parsed.access_token && parsed.refresh_token && parsed.user) {
      return parsed as unknown as Session;
    }
  } catch {
    console.error('[AuthPersistence] Failed to parse stored auth session');
  }
  return null;
}

export function getInitialBrowserAuthSession(): Session | null {
  if (isDesktopPlatform()) {
    return null;
  }

  return readLocalStorageSession();
}

/**
 * Removes the plaintext `localStorage` copy of the Supabase auth session.
 *
 * On desktop the session of record lives behind the bridge's OS keychain, so any
 * copy of it sitting in `localStorage` is legacy material with a refresh token
 * in it — readable off disk long after the session it belongs to ended.
 */
function clearPlaintextAuthStorage(): void {
  try {
    const storageKey = getSupabaseAuthTokenKey(import.meta.env.VITE_SUPABASE_URL);
    globalThis.localStorage.removeItem(storageKey);
    globalThis.localStorage.removeItem(`${storageKey}-code-verifier`);
    globalThis.localStorage.removeItem(`${storageKey}-user`);
  } catch {
    console.error('[AuthPersistence] Failed to clear plaintext auth storage');
  }
}

export async function ensureSupabaseAuthSession(
  supabase: SupabaseClient,
  fallbackSession: Session | null | undefined,
): Promise<boolean> {
  const {
    data: { session },
    error,
  } = await supabase.auth.getSession();

  if (!error && session?.access_token) {
    return true;
  }

  if (!hasSessionTokens(fallbackSession)) {
    return false;
  }

  const { error: setSessionError } = await supabase.auth.setSession({
    access_token: fallbackSession.access_token,
    refresh_token: fallbackSession.refresh_token,
  });
  if (setSessionError) {
    return false;
  }

  const {
    data: { session: hydratedSession },
    error: hydratedError,
  } = await supabase.auth.getSession();

  return !hydratedError && Boolean(hydratedSession?.access_token);
}

/** Purge the plaintext `localStorage` session left behind on desktop. Idempotent. */
export async function migrateLegacyAuthPersistence(): Promise<void> {
  if (isDesktopPlatform()) {
    clearPlaintextAuthStorage();
  }
  await Promise.resolve();
}

/**
 * Removes the desktop shell's auth session, which lives behind the bridge's
 * `authStorage` (an OS keychain in the Electron main process), not in
 * `localStorage`. Best-effort per key: one rejection must not strand the rest.
 */
async function clearDesktopBridgeAuthStorage(
  authStorage: NonNullable<ReturnType<typeof getDesktopBridge>>['authStorage'],
): Promise<void> {
  let storageKey: string;
  try {
    storageKey = getSupabaseAuthTokenKey(import.meta.env.VITE_SUPABASE_URL);
  } catch {
    console.error('[AuthPersistence] Cannot derive the desktop auth storage key');
    return;
  }
  for (const key of [storageKey, `${storageKey}-code-verifier`, `${storageKey}-user`]) {
    try {
      await authStorage.removeItem(key);
    } catch {
      console.error('[AuthPersistence] Failed to clear a desktop auth storage key');
    }
  }
}

/**
 * Destroys every local trace of the session, on whichever shell is running.
 *
 * The single owner of auth-storage teardown: callers MUST NOT hand-roll a
 * `localStorage.removeItem` of the Supabase token key alongside it. Each shell
 * keeps the session of record somewhere different — `localStorage` on web, the
 * bridge's `authStorage` on desktop (see `getSupabaseAuthStorage`) — and
 * clearing only one of them is indistinguishable from working on the platform
 * that uses the other.
 *
 * The plaintext `localStorage` copy is cleared on EVERY platform, not just the
 * one that treats it as the store of record: `-code-verifier` and `-user` sit
 * beside the token key.
 */
export async function clearAllAppData(): Promise<void> {
  clearPlaintextAuthStorage();

  const desktopBridge = getDesktopBridge();
  if (desktopBridge) {
    await clearDesktopBridgeAuthStorage(desktopBridge.authStorage);
  }
}
